import { api } from '@/services/http';
import { getCurrentWhitelist } from '@/services/singleWhitelistApi';
import { getWhitelistDownloadEndpoint } from '@/utils/downloadEndpoints';
import { parseWhitelistFilenamesFromBlob } from '@/utils/whitelistCsv';

export interface ActiveWhitelistFilenameSet {
  whitelistId: number;
  versionId: number;
  filename: string;
  fetchedAt: number;
  filenames: Set<string>;
}

const DEFAULT_TTL_MS = 5 * 60 * 1000;

let cacheEntry: ActiveWhitelistFilenameSet | null = null;
let inFlightKey: string | null = null;
let inFlight: Promise<ActiveWhitelistFilenameSet | null> | null = null;

function isCacheValid(entry: ActiveWhitelistFilenameSet, ttlMs: number): boolean {
  return Date.now() - entry.fetchedAt <= ttlMs;
}

function abortError(): DOMException {
  return new DOMException('Aborted', 'AbortError');
}

export async function getActiveWhitelistFilenameSet(options: {
  signal?: AbortSignal;
  ttlMs?: number;
} = {}): Promise<ActiveWhitelistFilenameSet | null> {
  const { signal, ttlMs = DEFAULT_TTL_MS } = options;
  if (signal?.aborted) throw abortError();

  // Always check current whitelist metadata first (fast request) to ensure cache is still current.
  const whitelist = await getCurrentWhitelist({ signal });
  if (signal?.aborted) throw abortError();

  if (!whitelist) {
    cacheEntry = null;
    return null;
  }

  const whitelistId = whitelist.id;
  const versionId = whitelist.last_version_id;
  const filename = whitelist.filename;
  const cacheKey = `${whitelistId}:${versionId}`;

  // If cached entry matches current whitelist version and is valid, reuse it.
  if (
    cacheEntry &&
    cacheEntry.whitelistId === whitelistId &&
    cacheEntry.versionId === versionId &&
    isCacheValid(cacheEntry, ttlMs)
  ) {
    return cacheEntry;
  }

  // Deduplicate concurrent downloads/parses for the same whitelist version.
  if (inFlight && inFlightKey === cacheKey) {
    return await inFlight;
  }

  inFlightKey = cacheKey;
  inFlight = (async () => {
    const endpoint = getWhitelistDownloadEndpoint(whitelist);

    const response = await api.get(endpoint, {
      responseType: 'blob',
      headers: { Accept: '*/*' },
      signal,
    });

    if (signal?.aborted) throw abortError();

    const blob = response.data as Blob;
    const filenames = await parseWhitelistFilenamesFromBlob(blob, { signal });

    const next: ActiveWhitelistFilenameSet = {
      whitelistId,
      versionId,
      filename,
      fetchedAt: Date.now(),
      filenames,
    };

    cacheEntry = next;
    return next;
  })().finally(() => {
    inFlight = null;
    inFlightKey = null;
  });

  return await inFlight;
}

export function invalidateActiveWhitelistFilenameSetCache(): void {
  cacheEntry = null;
}

export async function refreshActiveWhitelistFilenameSet(options: {
  signal?: AbortSignal;
} = {}): Promise<ActiveWhitelistFilenameSet | null> {
  invalidateActiveWhitelistFilenameSetCache();
  // ttlMs=0 forces re-download even if the version hasn't changed.
  return await getActiveWhitelistFilenameSet({ signal: options.signal, ttlMs: 0 });
}

